import type { DashboardTile } from '../dashboardTypes';
import type { CanonicalWidgetType, WidgetPreset, WidgetSpec } from './widgetTypes';

export const GRID_COLS = 12;
export const GRID_ROW_HEIGHT = 80;
export const GRID_MARGIN: [number, number] = [12, 12];
export const GRID_MAX_ROW_SPAN = 24;

export const PRESET_SIZE: Record<WidgetPreset, { w: number; h: number }> = {
  S: { w: 3, h: 3 },
  M: { w: 6, h: 4 },
  L: { w: 9, h: 5 },
  full: { w: 12, h: 5 },
};

export const DEFAULT_PRESET: Record<CanonicalWidgetType, WidgetPreset> = {
  'perf.scorecard': 'S',
  'perf.northStar': 'L',
  'perf.highlightsCard': 'M',
  'perf.situation': 'L',
  'perf.outletTable': 'full',
  'live.curve': 'full',
  'live.gantt': 'full',
  'live.ribKpis': 'M',
  'live.segbar': 'full',
  'live.occupancyRing': 'S',
  'live.briefGroups': 'M',
  'live.briefArrivals': 'M',
  'live.briefAttention': 'M',
  'live.recovery': 'L',
  'live.sell': 'M',
  'live.weather': 'M',
  'horizon.week': 'full',
  'horizon.hero': 'L',
  'horizon.signals': 'L',
};

export type GridRect = { x: number; y: number; w: number; h: number };

export function presetOfTile(tile: DashboardTile): WidgetPreset {
  if (tile.layout?.preset) return tile.layout.preset;
  if (tile.widget) return DEFAULT_PRESET[tile.widget.type];
  if (tile.kind === 'chapter') return 'full';
  return 'M';
}

export function sizeForPreset(preset: WidgetPreset): { w: number; h: number } {
  return PRESET_SIZE[preset];
}

export function presetForSize(w: number, h: number): WidgetPreset | null {
  const match = (Object.keys(PRESET_SIZE) as WidgetPreset[]).find(preset => {
    const size = PRESET_SIZE[preset];
    return size.w === w && size.h === h;
  });
  return match ?? null;
}

export function sizeOfTile(tile: DashboardTile): { w: number; h: number } {
  if (tile.layout?.w != null && tile.layout.h != null) {
    return { w: tile.layout.w, h: tile.layout.h };
  }
  return sizeForPreset(tile.layout?.preset ?? presetOfTile(tile));
}

export function rectsOverlap(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function firstFreeRect(
  occupied: GridRect[],
  w: number,
  h: number,
  cols = GRID_COLS,
): GridRect {
  const width = Math.min(w, cols);
  let y = 0;
  while (y < 400) {
    for (let x = 0; x <= cols - width; x += 1) {
      const candidate = { x, y, w: width, h };
      if (!occupied.some(item => rectsOverlap(item, candidate))) {
        return candidate;
      }
    }
    y += 1;
  }
  return { x: 0, y: 0, w: width, h };
}

export function clampPresetRect(
  x: number,
  y: number,
  preset: WidgetPreset,
  cols = GRID_COLS,
): GridRect {
  const { w, h } = sizeForPreset(preset);
  const width = Math.min(w, cols);
  return {
    x: Math.max(0, Math.min(x, cols - width)),
    y: Math.max(0, y),
    w: width,
    h,
  };
}

export function defaultPresetForSpec(spec: WidgetSpec | undefined): WidgetPreset {
  if (!spec) return 'M';
  return DEFAULT_PRESET[spec.type];
}

export function migrateTileLayout(tiles: DashboardTile[]): DashboardTile[] {
  const occupied: GridRect[] = [];
  return tiles.map(tile => {
    if (tile.layout) {
      const { w, h } = sizeOfTile(tile);
      occupied.push({ x: tile.layout.x, y: tile.layout.y, w, h });
      return tile;
    }
    const preset = presetOfTile(tile);
    const { w, h } = sizeForPreset(preset);
    const rect = firstFreeRect(occupied, w, h);
    occupied.push(rect);
    return {
      ...tile,
      layout: { x: rect.x, y: rect.y, preset },
    };
  });
}

export function placeNewTile(
  existing: DashboardTile[],
  preset: WidgetPreset,
): { x: number; y: number; preset: WidgetPreset } {
  const occupied = occupiedRects(existing);
  const size = sizeForPreset(preset);
  const rect = firstFreeRect(occupied, size.w, size.h);
  return { x: rect.x, y: rect.y, preset };
}

export function rectForTile(tile: DashboardTile): GridRect | null {
  if (!tile.layout) return null;
  const { w, h } = sizeOfTile(tile);
  return { x: tile.layout.x, y: tile.layout.y, w, h };
}

export function occupiedRects(
  tiles: DashboardTile[],
  ignoreId?: string,
): GridRect[] {
  const rects: GridRect[] = [];
  for (const tile of tiles) {
    if (ignoreId && tile.id === ignoreId) continue;
    const rect = rectForTile(tile);
    if (rect) rects.push(rect);
  }
  return rects;
}

export function canPlace(occupied: GridRect[], rect: GridRect): boolean {
  return !occupied.some(item => rectsOverlap(item, rect));
}

export function applyPresetInPlace(
  tile: DashboardTile,
  preset: WidgetPreset,
  others: DashboardTile[],
  cols = GRID_COLS,
): { ok: true; tile: DashboardTile } | { ok: false } {
  const current = tile.layout ?? { x: 0, y: 0, preset: 'M' as WidgetPreset };
  const { w, h } = sizeForPreset(preset);
  const rect = { x: current.x, y: Math.max(0, current.y), w: Math.min(w, cols), h };
  if (rect.x < 0 || rect.x + rect.w > cols) return { ok: false };
  if (!canPlace(occupiedRects(others), rect)) return { ok: false };
  return { ok: true, tile: { ...tile, layout: { x: rect.x, y: rect.y, preset } } };
}

export function applyCustomRect(
  tile: DashboardTile,
  next: GridRect,
  others: DashboardTile[],
  cols = GRID_COLS,
): { ok: true; tile: DashboardTile } | { ok: false } {
  const w = Math.round(next.w);
  const h = Math.round(next.h);
  const x = Math.round(next.x);
  const y = Math.round(next.y);
  if (
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    w < 1 ||
    w > cols ||
    h < 1 ||
    h > GRID_MAX_ROW_SPAN ||
    x < 0 ||
    y < 0 ||
    x + w > cols
  ) {
    return { ok: false };
  }
  const rect = { x, y, w, h };
  if (!canPlace(occupiedRects(others), rect)) return { ok: false };
  const match = presetForSize(w, h);
  const preset = match ?? tile.layout?.preset ?? 'M';
  return {
    ok: true,
    tile: {
      ...tile,
      layout: match ? { x, y, preset: match } : { x, y, preset, w, h },
    },
  };
}

export function nudgeTile(
  tile: DashboardTile,
  dx: number,
  dy: number,
  others: DashboardTile[],
  cols = GRID_COLS,
): { ok: true; tile: DashboardTile } | { ok: false } {
  if (!tile.layout) return { ok: false };
  const { w, h } = sizeOfTile(tile);
  const x = tile.layout.x + dx;
  const y = tile.layout.y + dy;
  if (x < 0 || y < 0 || x + w > cols) return { ok: false };
  const rect = { x, y, w, h };
  if (!canPlace(occupiedRects(others), rect)) return { ok: false };
  return { ok: true, tile: { ...tile, layout: { ...tile.layout, x, y } } };
}

export function packTiles(tiles: DashboardTile[]): DashboardTile[] {
  const occupied: GridRect[] = [];
  const packed = new Map<string, DashboardTile>();
  const readingOrder = [...tiles].sort((a, b) => {
    const ay = a.layout?.y ?? 0;
    const by = b.layout?.y ?? 0;
    if (ay !== by) return ay - by;
    return (a.layout?.x ?? 0) - (b.layout?.x ?? 0);
  });
  for (const tile of readingOrder) {
    const preset = tile.layout?.preset ?? presetOfTile(tile);
    const { w, h } = sizeOfTile(tile);
    const rect = firstFreeRect(occupied, w, h);
    occupied.push(rect);
    const custom = tile.layout?.w != null && tile.layout.h != null;
    packed.set(tile.id, {
      ...tile,
      layout: custom
        ? { x: rect.x, y: rect.y, preset, w, h }
        : { x: rect.x, y: rect.y, preset },
    });
  }
  return tiles.map(tile => packed.get(tile.id) ?? tile);
}
